import { GalleryConfig } from '../types/GalleryConfig';
import { Medium } from '../types/Medium';
import { Year } from '../types/Year';
import { Series } from '../types/Series';
import { ArtworkTag } from '../types/ArtworkTag';
import { ViewingRoomMode } from '../types/ViewingRoomMode';

// Mediums that count as "Works on Paper" (drives that section's Selected Works leaf).
const PAPER_MEDIUMS: Medium[] = [
    Medium.PastelOnPaper,
    Medium.MixedMediaOnWatercolorPaper,
    Medium.AcrylicInkOnPaper,
    Medium.AcrylicInkGouacheOnPaper,
    Medium.CharcoalPencilGouacheOnPaper,
    Medium.InkGouacheOnPaper,
    Medium.PastelCharcoalOnPaper,
];

const OIL_MEDIUMS: Medium[] = [
    Medium.OilOnCanvas,
    Medium.OilOnLinen,
    Medium.OilOnWoodPanel,
    Medium.OilOnBoard,
];

/**
 * The single editable information architecture. Edit this tree to re-curate
 * the gallery — the sidebar/drawer nav and every gallery route render from it. Membership
 * is declarative (`match`) or explicit (`artworkIds`); ordering within a leaf follows the
 * artwork's `order`. Seeds below (Featured tag set, curated paper Featured, year buckets)
 * are starting points — adjust freely.
 */
export const GALLERY_CONFIG: GalleryConfig = {
    defaultRoute: 'paintings/featured',
    showCounts: true,
    viewingRoom: ViewingRoomMode.Light,
    landing: {
        artworkId: 'uninhabited-disassembled-identity', // portrait / mobile hero
        objectPosition: 'center 30%',
        landscapeArtworkId: 'interference', // landscape / desktop hero (cropped to fill)
        // Desktop crop focal point — CSS object-position. Adjust to choose which part stays in view,
        // e.g. 'center', 'center top', 'center 30%', 'left center', '50% 20%'.
        landscapeObjectPosition: 'center 18%',
    },
    sections: [
        {
            slug: 'paintings',
            label: 'Paintings',
            children: [
                { slug: 'featured', label: 'Featured', match: { tags: [ArtworkTag.Featured], mediums: OIL_MEDIUMS } },
                {
                    slug: 'series',
                    label: 'Series',
                    children: [
                        {
                            slug: 'disassembled-identity',
                            label: 'Disassembled Identity',
                            match: { series: Series.DisassembledIdentity, mediums: OIL_MEDIUMS },
                        },
                        { slug: 'liminal-presence', label: 'Liminal Presence', match: { series: Series.LiminalPresence, mediums: OIL_MEDIUMS } },
                        { slug: 'boxed-in', label: 'Boxed In', match: { series: Series.BoxedIn, mediums: OIL_MEDIUMS } },
                    ],
                },
                {
                    slug: 'selected-works',
                    label: 'Selected Works',
                    children: [
                        { slug: '2026', label: '2026', match: { years: [Year.Y2026], tags: [ArtworkTag.SelectedWorks], mediums: OIL_MEDIUMS } },
                        { slug: '2025', label: '2025', match: { years: [Year.Y2025], tags: [ArtworkTag.SelectedWorks], mediums: OIL_MEDIUMS } },
                        { slug: '2024', label: '2024', match: { years: [Year.Y2024], tags: [ArtworkTag.SelectedWorks], mediums: OIL_MEDIUMS } },
                    ],
                },
            ],
        },
        {
            slug: 'works-on-paper',
            label: 'Works on Paper',
            children: [
                { slug: 'featured', label: 'Featured', match: { tags: [ArtworkTag.Featured], mediums: PAPER_MEDIUMS } },
                { slug: 'selected-works', label: 'Selected Works', match: { tags: [ArtworkTag.SelectedWorks], mediums: PAPER_MEDIUMS } },
            ],
        },
        { slug: 'about', label: 'About', href: '/about' },
        { slug: 'cv', label: 'CV', href: '/cv' },
        { slug: 'contact', label: 'Contact', href: '/contact' },
    ],
};
